import React, {
    useRef,
    useImperativeHandle,
    forwardRef,
    useState,
    useEffect
} from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import InvoiceTemplate from "./InvoiceTemplate";
import "./InvoiceTemplate.css";
import { getOption } from "../../api/optionsApi";

const InvoicePDF = forwardRef(({ order, styles }, ref) => {
    const targetRef = useRef(null);
    const [billFromData, setBillFromData] = useState();

    useEffect(() => {
        const fetchToolSettings = async () => {
            try {
                const billFromDataResponse = await getOption("billFrom");

                const billFromValue = billFromDataResponse?.data?.value;

                if (billFromValue) {
                    setBillFromData(billFromValue);
                }
            } catch (error) {
                console.log("Failed to load tool settings:", error);
            }
        };

        fetchToolSettings();
    }, []);

    // Get the CSS used by InvoiceTemplate so the print window
    // uses exactly the same UI/styles as InvoiceTemplate.
    const getInvoiceStyles = () => {
        const styleTexts = [];

        Array.from(document.styleSheets).forEach((styleSheet) => {
            try {
                const rules = styleSheet.cssRules;

                if (!rules) return;

                const cssText = Array.from(rules)
                    .map((rule) => rule.cssText)
                    .join("\n");

                if (
                    cssText.includes(".invoice-container") ||
                    cssText.includes(".invoice-title") ||
                    cssText.includes(".invoice-table")
                ) {
                    styleTexts.push(cssText);
                }
            } catch (error) {
                // Ignore stylesheets that the browser does not allow us to read.
            }
        });

        return styleTexts.join("\n");
    };

    // Expose generatePDFBlob function to parent component
    useImperativeHandle(
        ref,
        () => ({
            generatePDFBlob: async () => {
                try {
                    if (!targetRef.current) {
                        console.error("Target ref not found");
                        return null;
                    }

                    const element = targetRef.current;

                    // Keep the existing hidden rendering behaviour.
                    const originalStyle = element.getAttribute("style");

                    element.style.position = "fixed";
                    element.style.left = "0";
                    element.style.top = "0";
                    element.style.zIndex = "-9999";

                    const canvas = await html2canvas(element, {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        allowTaint: true,
                        backgroundColor: "#ffffff"
                    });

                    // Restore original style
                    element.setAttribute("style", originalStyle || "");

                    const imgData = canvas.toDataURL("image/jpeg", 1.0);

                    const pdf = new jsPDF({
                        orientation: "portrait",
                        unit: "mm",
                        format: "a4"
                    });

                    const imgWidth = 210;
                    const pageHeight = 297;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;

                    let heightLeft = imgHeight;
                    let position = 0;

                    pdf.addImage(
                        imgData,
                        "JPEG",
                        0,
                        position,
                        imgWidth,
                        imgHeight
                    );

                    heightLeft -= pageHeight;

                    while (heightLeft > 0) {
                        position = heightLeft - imgHeight;

                        pdf.addPage();

                        pdf.addImage(
                            imgData,
                            "JPEG",
                            0,
                            position,
                            imgWidth,
                            imgHeight
                        );

                        heightLeft -= pageHeight;
                    }

                    return pdf.output("blob");
                } catch (error) {
                    console.error("Error generating PDF:", error);
                    return null;
                }
            }
        }),
        [order]
    );

    const handlePrint = () => {
        if (!targetRef.current) {
            console.error("Invoice template not found");
            return;
        }

        const printWindow = window.open(
            "",
            "_blank",
            "width=1000,height=800"
        );

        if (!printWindow) {
            console.error("Unable to open print window");
            return;
        }

        const invoiceElement = targetRef.current.cloneNode(true);

        // Remove the hidden/off-screen positioning
        invoiceElement.style.position = "static";
        invoiceElement.style.left = "auto";
        invoiceElement.style.top = "auto";
        invoiceElement.style.zIndex = "auto";

        const invoiceStyles = getInvoiceStyles();

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <meta charset="UTF-8" />

                <title>
                    Invoice - ${order?.orderId || "Print"}
                </title>

                <style>
                    ${invoiceStyles}

                    /* =========================
                       PRINT PAGE RESET
                    ========================= */

                    html,
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        background: #ffffff !important;
                    }

                    body {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    #print-root {
                        width: 100%;
                        margin: 0;
                        padding: 0;
                    }

                    /* =========================
                       IMPORTANT:
                       FORCE INVOICE TO A4 WIDTH
                    ========================= */

                    .invoice-container {
                        width: 210mm !important;
                        max-width: 210mm !important;

                        /*
                         * 10mm padding on each side.
                         * box-sizing prevents padding from
                         * increasing the A4 width.
                         */
                        padding: 10mm !important;

                        margin: 0 auto !important;

                        box-sizing: border-box !important;

                        background: #ffffff !important;

                        overflow: visible !important;
                    }

                    /* =========================
                       DO NOT CLIP TABLE
                    ========================= */

                    .table-container {
                        width: 100% !important;
                        max-width: 100% !important;

                        overflow: visible !important;

                        margin-bottom: 15px !important;
                    }

                    .invoice-table {
                        width: 100% !important;
                        max-width: 100% !important;

                        table-layout: fixed;
                    }

                    .invoice-table th,
                    .invoice-table td {
                        overflow-wrap: break-word;
                        word-break: break-word;
                    }

                    /* =========================
                       KEEP SECTIONS TOGETHER
                    ========================= */

                    .invoice-header {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .shipping-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .summary-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .bank-details {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .invoice-table tr {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .terms-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    .declaration-section {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }

                    /* =========================
                       A4 PRINT SETTINGS
                    ========================= */

                    @page {
                        size: A4 portrait;
                        margin: 0;
                    }

                    @media print {

                        html,
                        body {
                            width: 210mm !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }

                        #print-root {
                            width: 210mm !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }

                        .invoice-container {
                            width: 210mm !important;
                            max-width: 210mm !important;
                            margin: 0 !important;
                            padding: 10mm !important;

                            box-sizing: border-box !important;

                            overflow: visible !important;
                        }

                        .table-container {
                            overflow: visible !important;
                        }

                        .invoice-table {
                            width: 100% !important;
                        }

                        /*
                         * Allow the document to continue naturally
                         * onto another page if the content genuinely
                         * exceeds one A4 page.
                         */
                        .invoice-container {
                            break-inside: auto;
                            page-break-inside: auto;
                        }

                        /*
                         * Prevent individual rows from being split.
                         */
                        .invoice-table tr {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }

                        /*
                         * Keep these sections together when possible.
                         */
                        .summary-section,
                        .bank-details,
                        .terms-section,
                        .declaration-section {
                            break-inside: avoid;
                            page-break-inside: avoid;
                        }
                    }
                </style>
            </head>

            <body>
                <div id="print-root"></div>
            </body>
        </html>
    `);

        printWindow.document.close();

        const printRoot = printWindow.document.getElementById(
            "print-root"
        );

        if (!printRoot) {
            printWindow.close();
            return;
        }

        printRoot.appendChild(invoiceElement);

        /*
         * Wait for all invoice images to load.
         */
        const waitForImages = Array.from(
            printWindow.document.images || []
        ).map((image) => {
            if (image.complete) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                image.onload = resolve;
                image.onerror = resolve;
            });
        });

        Promise.all(waitForImages).then(() => {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);
        });
    };

    return (
        <div>
            <button className={styles?.addBtn} onClick={handlePrint}>
                <p>Print Invoice</p>
            </button>

            <div
                style={{
                    position: "absolute",
                    left: "-9999px",
                    top: 0
                }}
            >
                <InvoiceTemplate
                    ref={targetRef}
                    order={order}
                    billFromData={billFromData}
                />
            </div>
        </div>
    );
});

InvoicePDF.displayName = "InvoicePDF";

export default InvoicePDF;
